import { apiOpenStream } from './api';

export interface StreamHandlers {
  signal: AbortSignal;
  onReady?: (summary: unknown) => void;
  onChanged?: (summary: unknown) => void;
  onReconcile?: (summary: unknown) => void;
  onError?: (error: unknown) => void;
}

interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse a buffered SSE stream, yielding complete events. Handles arbitrary chunk
 * boundaries, CRLF/LF, multi-line `data:` fields, comments (`:`) and UTF-8.
 * Exported for unit testing.
 */
export function createSseParser() {
  let buffer = '';
  return {
    push(chunk: string): SseEvent[] {
      buffer += chunk;
      const events: SseEvent[] = [];
      // Event boundaries are a blank line, in either LF or CRLF form.
      let idx: number;
      while ((idx = buffer.search(/\r?\n\r?\n/)) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + buffer.slice(idx).match(/^\r?\n\r?\n/)![0].length);
        const parsed = parseRaw(raw);
        if (parsed) events.push(parsed);
      }
      return events;
    },
    flush(): SseEvent[] {
      const parsed = parseRaw(buffer);
      buffer = '';
      return parsed ? [parsed] : [];
    },
  };
}

function parseRaw(raw: string): SseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith(':')) continue; // comment / heartbeat
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0 && event === 'message') return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Connect to the notification SSE stream. Resolves when the stream ends; the
 * caller owns reconnection/backoff. Each named event is dispatched to its
 * handler; malformed payloads are ignored rather than throwing.
 */
export async function connectNotificationStream(handlers: StreamHandlers): Promise<void> {
  const res = await apiOpenStream('/api/notifications/stream', handlers.signal);
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  const parser = createSseParser();

  try {
    while (!handlers.signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const evt of parser.push(chunk)) dispatch(evt, handlers);
    }
    for (const evt of parser.flush()) dispatch(evt, handlers);
  } finally {
    reader.releaseLock();
  }
}

function dispatch(evt: { event: string; data: string }, handlers: StreamHandlers): void {
  let payload: unknown;
  try {
    payload = evt.data ? JSON.parse(evt.data) : undefined;
  } catch {
    return; // ignore malformed
  }
  switch (evt.event) {
    case 'ready':
      handlers.onReady?.(payload);
      break;
    case 'changed':
      handlers.onChanged?.(payload);
      break;
    case 'reconcile':
      handlers.onReconcile?.(payload);
      break;
    default:
      break;
  }
}
